import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { emergencyStop, fetchControls, resumeTrading } from '@/api/endpoints';
import type { KillSwitchState } from '@/api/types';
import { RequirePermission } from '@/components/RequirePermission';
import { describe } from '@/lib/errors';

const EXCHANGES = ['binance', 'bitget', 'bybit', 'gate', 'kucoin'];

function label(item: KillSwitchState): string {
  if (item.scope === 'GLOBAL') return 'All trading';
  if (item.scope === 'SYMBOL') return `${item.scope_ref ?? ''} on every exchange`;
  if (item.scope === 'EXCHANGE') return `Everything on ${item.scope_ref ?? ''}`;
  return `${item.scope.toLowerCase()} ${item.scope_ref ?? ''}`;
}

/**
 * Kill switches.
 *
 * Stopping is one action with a reason, deliberately: it should never be the
 * harder option. Releasing needs a reason too, and a separate permission.
 */
export function ControlsPage() {
  const queryClient = useQueryClient();
  const controls = useQuery({ queryKey: ['controls'], queryFn: fetchControls });
  const [symbol, setSymbol] = useState('');
  const [symbolReason, setSymbolReason] = useState('');
  const [exchange, setExchange] = useState('');
  const [exchangeReason, setExchangeReason] = useState('');
  const [globalReason, setGlobalReason] = useState('');
  const [releaseReason, setReleaseReason] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['controls'] });

  const stop = useMutation({
    mutationFn: (args: { scope: string; ref?: string; reason: string }) =>
      emergencyStop(args.reason, args.scope, args.ref) as Promise<{
        scope_ref: string | null;
        orders_cancel_requested?: number;
      }>,
    onSuccess: (data, args) => {
      setError(null);
      const cancelled = data.orders_cancel_requested ?? 0;
      setNotice(
        `${args.scope === 'GLOBAL' ? 'All trading' : (data.scope_ref ?? args.ref ?? '')} blocked.` +
          (cancelled ? ` ${cancelled} open order${cancelled === 1 ? '' : 's'} being cancelled.` : ''),
      );
      setSymbol('');
      setSymbolReason('');
      setExchangeReason('');
      setGlobalReason('');
      refresh();
    },
    onError: (err) => setError(describe(err)),
  });

  const release = useMutation({
    mutationFn: (item: KillSwitchState) =>
      resumeTrading(
        releaseReason[`${item.scope}:${item.scope_ref ?? ''}`] ?? '',
        item.scope,
        item.scope_ref ?? undefined,
      ),
    onSuccess: (_data, item) => {
      setError(null);
      setNotice(`${label(item)} released.`);
      refresh();
    },
    onError: (err) => setError(describe(err)),
  });

  const active = (controls.data?.switches ?? []).filter((item) => item.active);

  return (
    <>
      <div className="page-head">
        <h1>Controls</h1>
        <p>
          A kill switch stops new orders in its scope and cancels the open orders it covers. It
          does not sell anything: to get out of a position, use Emergency exit on Positions.
        </p>
      </div>

      {error ? <div className="notice notice--error">{error}</div> : null}
      {notice ? <div className="notice">{notice}</div> : null}

      <section className="panel">
        <div className="panel-head">
          <h2>In force</h2>
        </div>
        <div className="panel-body flush">
          {active.length === 0 ? (
            <div className="empty">No kill switch is engaged. Trading runs normally.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Blocked</th>
                  <th>Reason</th>
                  <th>Since</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {active.map((item) => {
                  const key = `${item.scope}:${item.scope_ref ?? ''}`;
                  return (
                    <tr key={key} data-testid={`switch-${key}`}>
                      <td>{label(item)}</td>
                      <td>{item.reason ?? '—'}</td>
                      <td className="num">
                        {item.activated_at ? new Date(item.activated_at).toLocaleString() : '—'}
                      </td>
                      <td className="right">
                        <RequirePermission permission="control:trading">
                          <div className="row-actions">
                            <input
                              value={releaseReason[key] ?? ''}
                              onChange={(e) =>
                                setReleaseReason((current) => ({ ...current, [key]: e.target.value }))
                              }
                              placeholder="why release it"
                              aria-label={`Reason to release ${label(item)}`}
                            />
                            <button
                              type="button"
                              disabled={!(releaseReason[key] ?? '').trim() || release.isPending}
                              onClick={() => release.mutate(item)}
                            >
                              Release
                            </button>
                          </div>
                        </RequirePermission>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <RequirePermission permission="control:emergency_stop">
        <section className="panel">
          <div className="panel-head">
            <h2>Block a symbol</h2>
          </div>
          <div className="panel-body">
            <p style={{ color: 'var(--text-secondary)' }}>
              Stops every new order in the pair on every exchange, until released.
            </p>
            <div className="assign">
              <label>
                <span>Symbol</span>
                <input
                  value={symbol}
                  onChange={(e) => setSymbol(e.target.value)}
                  placeholder="COIN/USDT"
                />
              </label>
              <label>
                <span>Reason</span>
                <input
                  value={symbolReason}
                  onChange={(e) => setSymbolReason(e.target.value)}
                  placeholder="what is wrong with it"
                />
              </label>
              <button
                type="button"
                className="danger"
                disabled={!symbol.trim() || !symbolReason.trim() || stop.isPending}
                onClick={() =>
                  stop.mutate({ scope: 'SYMBOL', ref: symbol.trim(), reason: symbolReason.trim() })
                }
              >
                Block symbol
              </button>
            </div>
          </div>
        </section>

        <section className="panel">
          <div className="panel-head">
            <h2>Block an exchange</h2>
          </div>
          <div className="panel-body">
            <div className="assign">
              <label>
                <span>Exchange</span>
                <select value={exchange} onChange={(e) => setExchange(e.target.value)}>
                  <option value="">choose…</option>
                  {EXCHANGES.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Reason</span>
                <input
                  value={exchangeReason}
                  onChange={(e) => setExchangeReason(e.target.value)}
                  placeholder="what is wrong"
                />
              </label>
              <button
                type="button"
                className="danger"
                disabled={!exchange || !exchangeReason.trim() || stop.isPending}
                onClick={() =>
                  stop.mutate({ scope: 'EXCHANGE', ref: exchange, reason: exchangeReason.trim() })
                }
              >
                Block exchange
              </button>
            </div>
          </div>
        </section>

        <section className="panel">
          <div className="panel-head">
            <h2>Stop all trading</h2>
          </div>
          <div className="panel-body">
            <div className="assign">
              <label>
                <span>Reason</span>
                <input
                  value={globalReason}
                  onChange={(e) => setGlobalReason(e.target.value)}
                  placeholder="why"
                />
              </label>
              <button
                type="button"
                className="danger"
                disabled={!globalReason.trim() || stop.isPending}
                onClick={() => stop.mutate({ scope: 'GLOBAL', reason: globalReason.trim() })}
              >
                Emergency stop
              </button>
            </div>
          </div>
        </section>
      </RequirePermission>
    </>
  );
}
